import { Box, Button, Heading, LoadingDots } from '@weareberyl/design-system'
import {
  Button as AntDButton,
  Checkbox,
  Col,
  Form,
  Modal,
  Radio,
  Row,
  Switch,
  TimePicker,
} from 'antd'
import { AbstractCheckboxGroupProps } from 'antd/lib/checkbox/Group'
import moment, { Moment } from 'moment'
import { useEffect, useState } from 'react'

import { useCachedSchemeConfigWithDefault } from '~/context/currentScheme'
import {
  AdjustEndJourneyMutationVariables,
  EndJourneyActionType,
  EndJourneyExtraAction,
  JourneySummaryFragment,
  ParkingStatus,
  VehicleLockState,
  useAdjustEndJourneyMutation,
  useJourneyPreviewFragment,
  useJourneySummaryFragment,
} from '~/gql/generated/graphql'
import {
  ParkingStatusWithFinesSelect,
  VehicleLockStateWithFinesSelect,
} from './components'
import { useJourneyVehicleData } from './data'

type EndProps = {
  journeyId: string
}

type JourneyWithScheme = JourneySummaryFragment & {
  scheme: NonNullable<JourneySummaryFragment['scheme']>
}

type FormData = Omit<AdjustEndJourneyMutationVariables, 'journeyId'> & {
  actionType: EndJourneyActionType
  // In the form this is a Moment instance, but it will be serialied
  endedAt: Moment | null
  overrideTime: boolean
}

const EndJourneyModalContent = ({
  journeyId,
  loading,
  saving,
  onFinish,
}: {
  journeyId: string
  loading: boolean
  saving: boolean
  onFinish: (formData: FormData) => void
}) => {
  // JourneyPreview is fetched by the list query, but JourneySummary gets re-fetched
  // when the modal opens. We can use data from the cache and it will update when the
  // summary query loads, to make sure we have an up to date state. i.e. on_hold or whatever
  const { data: journey } = useJourneyPreviewFragment({
    id: journeyId,
  })
  // It should be complete, but this narrows the type of journey, not to be a deep partial (or null)
  if (!journey) {
    throw new Error('Really thought there would be a journey')
  }

  // Get info about the vehicle used on the journey
  const { hasELock, hasAssist } = useJourneyVehicleData(journeyId)

  // Get pricing for journey scheme
  const journeyWithScheme = journey as JourneyWithScheme
  // use journey scheme to get config from ApolloCache
  const { scheme_pricing_configs } = useCachedSchemeConfigWithDefault(
    journeyWithScheme.scheme.id,
  )

  const isOnHold = journey.state?.message === 'on_hold'

  const [form] = Form.useForm<FormData>()
  const actionType = Form.useWatch('actionType', form)
  const overrideTime = Form.useWatch('overrideTime', form)
  const initialValues: FormData = {
    actionType: EndJourneyActionType.end_journey,
    extraActions: [],
    overrideTime: false,
    endedAt: null,
    vehicleLockState: VehicleLockState.locked,
    parkingStatus: ParkingStatus.valid,
  }

  if (hasELock) {
    initialValues.extraActions.push(EndJourneyExtraAction.safe_lock)
  }
  if (hasAssist) {
    initialValues.extraActions.push(EndJourneyExtraAction.disable_assist)
  }

  // Initially set current time if override is enabled
  useEffect(() => {
    if (overrideTime) {
      form.setFieldValue('endedAt', moment())
    } else {
      form.setFieldValue('endedAt', null)
    }
  }, [overrideTime, form])

  const endJourneyTypeOptions: AbstractCheckboxGroupProps['options'] = [
    { label: 'End as normal', value: EndJourneyActionType.end_journey },
    {
      label: 'Fixed penalty',
      value: EndJourneyActionType.cancel_journey,
      disabled: isOnHold,
    },
    {
      label: 'Void journey',
      value: EndJourneyActionType.void_journey,
      disabled: isOnHold,
    },
  ]

  const extraActionOptions: AbstractCheckboxGroupProps['options'] = [
    { label: 'Try to lock vehicle', value: 'safe_lock', disabled: !hasELock },
    {
      label: 'Disable assist',
      value: 'disable_assist',
      disabled: !hasAssist,
    },
  ]

  const actionTypeHints: Record<EndJourneyActionType, string | undefined> = {
    cancel_journey: `End journey with a flat ${scheme_pricing_configs.unlocked_vehicle_charge.formatted_amount} fine`,
    void_journey: 'End journey with no charges',
    end_journey: undefined,
  }

  const actionTypeHelpText = isOnHold
    ? 'Journey is on hold'
    : actionTypeHints[actionType]

  const canAdjust = actionType === EndJourneyActionType.end_journey

  const formDisabled = ![
    'paused',
    'on_hold',
    'partially_locked',
    'bike_unlocked',
  ].includes(journey.state?.message ?? '')

  return (
    <Box accessibilityRole="modal">
      <Heading variant="h1" mt={3} mx={3}>
        End journey
        {loading && <LoadingDots size={8} />}
      </Heading>
      <Box mt={3} mx={3}>
        <Form<FormData>
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={onFinish}
          disabled={formDisabled}
        >
          <Form.Item
            name="actionType"
            label="End journey"
            help={actionTypeHelpText}
          >
            <Radio.Group
              options={endJourneyTypeOptions}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>

          <Form.Item name="extraActions" label="Extra actions">
            <Checkbox.Group
              disabled={formDisabled}
              options={extraActionOptions}
            />
          </Form.Item>

          {canAdjust && (
            <Box mb={2}>
              <Row gutter={20}>
                <Col>
                  <Form.Item name="overrideTime" label="Override end time">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="endedAt" label="Adjusted end time">
                    <TimePicker disabled={!overrideTime} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="parkingStatus"
                label="Set parking status"
                help="Prevent or force a parking fine"
              >
                <ParkingStatusWithFinesSelect
                  schemePricingConfigs={scheme_pricing_configs}
                />
              </Form.Item>

              <Form.Item name="vehicleLockState" label="Set lock state">
                <VehicleLockStateWithFinesSelect
                  schemePricingConfigs={scheme_pricing_configs}
                  lockConfiguration={
                    journey.vehicle?.detail?.lock_configuration
                  }
                />
              </Form.Item>
            </Box>
          )}

          <Form.Item>
            <AntDButton type="primary" htmlType="submit" loading={saving}>
              End journey now
            </AntDButton>
          </Form.Item>
        </Form>
      </Box>
    </Box>
  )
}

export const EndJourneyModal = ({ journeyId }: EndProps) => {
  // Load this journey again, to get an up to date state
  const { data } = useJourneySummaryFragment({ id: journeyId })
  const [adjustEndJourney, { loading: saving }] = useAdjustEndJourneyMutation()

  const onFormFinish = (formData: FormData) => {
    const { actionType, overrideTime, ...adjustmentFields } = formData
    const adjustments: Omit<
      AdjustEndJourneyMutationVariables,
      'journeyId' | 'actionType'
    > = {
      ...adjustmentFields,
      endedAt:
        overrideTime && adjustmentFields.endedAt
          ? adjustmentFields.endedAt.toISOString()
          : null,
    }
    adjustEndJourney({ variables: { journeyId, actionType, ...adjustments } })
  }
  return (
    <EndJourneyModalContent
      journeyId={journeyId}
      onFinish={onFormFinish}
      loading={!data}
      saving={saving}
    />
  )
}

export const EndJourneyButton = ({ journeyId: id }: EndProps) => {
  const [visible, setVisible] = useState(false)
  const hideModal = () => {
    setVisible(false)
  }

  return (
    <>
      <Button
        testID="end-journey-btn"
        title="End&nbsp;journey"
        variant="outline"
        onPress={() => setVisible(true)}
      />

      <Modal
        open={visible}
        zIndex={1020}
        footer={null}
        onCancel={hideModal}
        width={550}
      >
        {visible && <EndJourneyModal journeyId={id} />}
      </Modal>
    </>
  )
}
